// src/components/HomeHorizontalTrainerList.tsx
import React, { useRef } from "react";
import Title from "./Title";
import { TertiaryButton } from "./Button";
import trainerImg from "../assets/trainer-placeholder.png";
import OverflowHeader from "./OverflowHeader";

/* ---------- types ---------- */
export type HomeTrainer = {
  name: string;
  rate: number; // credits per hour
  age: number;
  sex: "M" | "F";
  schedule: { day: string; time: string }[];
  imageUrl?: string; // deprecated - use trainingPhotos instead
  trainingPhotos?: string[]; // array of training photos from API
};

type Props = {
  trainers: HomeTrainer[];
  onCardClick: (trainer: HomeTrainer, index: number) => void;
  onExploreAll: () => void;
  gradient?: "red" | "dark";
  title?: string;
};


/* ---------- list (horizontal carousel) ---------- */
const HomeHorizontalTrainerList: React.FC<Props> = ({
  trainers,
  onCardClick,
  onExploreAll,
  gradient = "red",
  title = "// Explore Trainers",
}) => {
  // ── drag-to-scroll with inertia ───────────────────────────────────────────
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const hasDragged = useRef(false);
  const velocity = useRef(0);
  const lastX = useRef(0);
  const animFrame = useRef<number | null>(null);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    if (animFrame.current !== null) cancelAnimationFrame(animFrame.current);
    isDragging.current = true;
    hasDragged.current = false;
    velocity.current = 0;
    lastX.current = e.pageX;
    dragStartX.current = e.pageX - el.getBoundingClientRect().left;
    dragScrollLeft.current = el.scrollLeft;
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const x = e.pageX - el.getBoundingClientRect().left;
    const walk = x - dragStartX.current;
    velocity.current = e.pageX - lastX.current;
    lastX.current = e.pageX;
    if (Math.abs(walk) > 4) hasDragged.current = true;
    el.scrollLeft = dragScrollLeft.current - walk;
  };

  const onMouseUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const el = scrollRef.current;
    if (el) { el.style.cursor = "grab"; el.style.userSelect = ""; }
    // inertia glide
    let v = velocity.current * 0.8;
    const glide = () => {
      const el = scrollRef.current;
      if (!el || Math.abs(v) < 0.5) return;
      el.scrollLeft -= v;
      v *= 0.92;
      animFrame.current = requestAnimationFrame(glide);
    };
    animFrame.current = requestAnimationFrame(glide);
  };
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <section className="px-5 pt-12">
      <Title text={title} />

      <div
        ref={scrollRef}
        className="mt-3 flex gap-4 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden cursor-grab select-none"
        style={{ WebkitOverflowScrolling: "touch" }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {trainers.map((t, i) => (
          <div
            key={`${t.name}-${i}`}
            className="w-[280px] flex-shrink-0 overflow-visible bg-transparent cursor-pointer"
            onClick={() => {
              if (hasDragged.current) return;
              onCardClick(t, i);
            }}
          >
            {/* HEADER: transparent above gradient, no borders anywhere here */}
            <OverflowHeader
              name={t.name}
              rate={t.rate}
              gradient={gradient}
              meta={`${t.age}${t.sex}`}
              imageSrc={String(t.imageUrl || trainerImg)}
              imgHeight={140}   // image height (controls "peek above")
              bandHeight={100}  // visible gradient band
            />

            {/* SCHEDULE BOX: the only element with a border */}
            <div className="px-3 py-2 bg-white border border-[#DEDEDE] rounded">
              {(t.schedule ?? []).slice(0, 3).map((s, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between mx-2 my-1"
                >
                  <span
                    style={{
                      fontFamily: 'Manrope',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '150%',
                      letterSpacing: '0%',
                      textAlign: 'center',
                      color: '#717171'
                    }}
                  >
                    {s.day}
                  </span>
                  <span
                    style={{
                      fontFamily: 'Manrope',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '150%',
                      letterSpacing: '0%',
                      textAlign: 'center',
                      color: s.time.toLowerCase() === "busy" ? '#B24025' : '#252525'
                    }}
                  >
                    {s.time}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <TertiaryButton
          label="EXPLORE ALL"
          layout="split"
          onClick={onExploreAll}
          className="!w-full"
        />
      </div>
    </section>
  );
};

export default HomeHorizontalTrainerList;
